import { type ComponentProps, createContext, use } from 'react';
import {
  Pressable,
  type PressableProps,
  type StyleProp,
  type View,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Text from '@/components/UI/Text';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'positive'
  | 'negative'
  | 'success'
  | 'error'
  | 'warning'
  | 'info';
type ButtonAppearance = 'solid' | 'outline' | 'ghost';
type ButtonShape = 'pill' | 'circle';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type ButtonContextValue = {
  appearance: ButtonAppearance;
  size: ButtonSize;
  variant: ButtonVariant;
};

type ButtonStyleProps = {
  alignSelf?: ViewStyle['alignSelf'];
  backgroundColor?: ViewStyle['backgroundColor'];
  borderBottomWidth?: ViewStyle['borderBottomWidth'];
  borderColor?: ViewStyle['borderColor'];
  borderRadius?: ViewStyle['borderRadius'];
  borderWidth?: ViewStyle['borderWidth'];
  flex?: number | boolean;
  gap?: ViewStyle['gap'];
  height?: ViewStyle['height'];
  maxWidth?: ViewStyle['maxWidth'];
  minHeight?: ViewStyle['minHeight'];
  minWidth?: ViewStyle['minWidth'];
  opacity?: ViewStyle['opacity'];
  padding?: ViewStyle['padding'];
  paddingBottom?: ViewStyle['paddingBottom'];
  paddingHorizontal?: ViewStyle['paddingHorizontal'];
  paddingLeft?: ViewStyle['paddingLeft'];
  paddingRight?: ViewStyle['paddingRight'];
  paddingTop?: ViewStyle['paddingTop'];
  paddingVertical?: ViewStyle['paddingVertical'];
  width?: ViewStyle['width'];
};

interface ButtonProps
  extends Omit<
      PressableProps,
      | keyof ButtonStyleProps
      | 'accessibilityRole'
      | 'children'
      | 'disabled'
      | 'ref'
      | 'style'
    >,
    ButtonStyleProps {
  appearance?: ButtonAppearance;
  children?: React.ReactNode;
  disabled?: boolean;
  iconOnly?: boolean;
  ref?: React.Ref<View>;
  shape?: ButtonShape;
  size?: ButtonSize;
  style?: StyleProp<ViewStyle>;
  variant?: ButtonVariant;
}

interface ButtonTextProps
  extends Omit<ComponentProps<typeof Text>, 'variant' | 'size'> {
  variant?: ComponentProps<typeof Text>['variant'];
  size?: ComponentProps<typeof Text>['size'];
}

const ButtonContext = createContext<ButtonContextValue | null>(null);

function ButtonRoot({
  appearance = 'solid',
  children,
  disabled,
  iconOnly,
  ref,
  shape,
  size = 'md',
  style: extraStyles,
  variant = 'primary',
  alignSelf,
  backgroundColor,
  borderBottomWidth,
  borderColor,
  borderRadius,
  borderWidth,
  flex,
  gap,
  height,
  maxWidth,
  minHeight,
  minWidth,
  opacity,
  padding,
  paddingBottom,
  paddingHorizontal,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingVertical,
  width,
  ...rest
}: ButtonProps) {
  styles.useVariants({
    appearance,
    disabled,
    iconOnly,
    shape,
    size,
    variant,
  });

  return (
    <ButtonContext value={{ appearance, size, variant }}>
      <Pressable
        {...rest}
        ref={ref}
        accessibilityRole="button"
        disabled={disabled}
        pointerEvents={disabled ? 'none' : 'auto'}
        style={({ pressed }) => [
          styles.button,
          styles.overrides({
            alignSelf,
            backgroundColor,
            borderBottomWidth,
            borderColor,
            borderRadius,
            borderWidth,
            flex,
            gap,
            height,
            maxWidth,
            minHeight,
            minWidth,
            opacity,
            padding,
            paddingBottom,
            paddingHorizontal,
            paddingLeft,
            paddingRight,
            paddingTop,
            paddingVertical,
            width,
          }),
          pressed && styles.active,
          extraStyles,
        ]}
      >
        {children}
      </Pressable>
    </ButtonContext>
  );
}

function ButtonText({
  children,
  numberOfLines = 1,
  variant,
  size,
  ...props
}: ButtonTextProps) {
  const context = use(ButtonContext);
  const textVariant =
    variant ??
    buttonTextVariants[context?.appearance ?? 'solid'][
      context?.variant ?? 'primary'
    ];
  const textSize = size ?? buttonTextSizes[context?.size ?? 'md'];

  return (
    <Text
      {...props}
      bold
      numberOfLines={numberOfLines}
      size={textSize}
      variant={textVariant}
    >
      {children}
    </Text>
  );
}

const Button = Object.assign(ButtonRoot, {
  Text: ButtonText,
});

export default Button;

const outlineTextVariants = {
  error: 'error',
  info: 'info',
  negative: 'error',
  positive: 'success',
  primary: 'primary',
  secondary: 'secondary',
  success: 'success',
  warning: 'warning',
} satisfies Record<ButtonVariant, ComponentProps<typeof Text>['variant']>;

const buttonTextVariants = {
  ghost: outlineTextVariants,
  outline: outlineTextVariants,
  solid: {
    error: 'white',
    info: 'white',
    negative: 'white',
    positive: 'white',
    primary: 'white',
    secondary: 'white',
    success: 'white',
    warning: 'white',
  },
} satisfies Record<
  ButtonAppearance,
  Record<ButtonVariant, ComponentProps<typeof Text>['variant']>
>;

const buttonTextSizes = {
  lg: 'lg',
  md: 'md',
  sm: 'sm',
  xl: '2xl',
  xs: 'xs',
} satisfies Record<ButtonSize, ComponentProps<typeof Text>['size']>;

const styles = StyleSheet.create((theme) => ({
  active: {
    variants: {
      appearance: {
        ghost: {
          backgroundColor: theme.colors.backgroundSecondary,
        },
        outline: {},
        solid: {},
      },
      variant: {
        error: {
          backgroundColor: theme.colors.errorActive,
        },
        info: {
          backgroundColor: theme.colors.infoActive,
        },
        negative: {
          backgroundColor: theme.colors.errorActive,
        },
        positive: {
          backgroundColor: theme.colors.successActive,
        },
        primary: {
          backgroundColor: theme.colors.primaryActive,
        },
        secondary: {
          backgroundColor: theme.colors.secondaryActive,
        },
        success: {
          backgroundColor: theme.colors.successActive,
        },
        warning: {
          backgroundColor: theme.colors.warningActive,
        },
      },
    },
    compoundVariants: [
      {
        appearance: 'ghost',
        styles: {
          backgroundColor: theme.colors.backgroundSecondary,
        },
      },
      {
        appearance: 'outline',
        variant: 'error',
        styles: {
          backgroundColor: theme.colors.errorHover,
        },
      },
      {
        appearance: 'outline',
        variant: 'info',
        styles: {
          backgroundColor: theme.colors.infoHover,
        },
      },
      {
        appearance: 'outline',
        variant: 'negative',
        styles: {
          backgroundColor: theme.colors.errorHover,
        },
      },
      {
        appearance: 'outline',
        variant: 'positive',
        styles: {
          backgroundColor: theme.colors.successHover,
        },
      },
      {
        appearance: 'outline',
        variant: 'primary',
        styles: {
          backgroundColor: theme.colors.primaryHover,
        },
      },
      {
        appearance: 'outline',
        variant: 'secondary',
        styles: {
          backgroundColor: theme.colors.secondaryHover,
        },
      },
      {
        appearance: 'outline',
        variant: 'success',
        styles: {
          backgroundColor: theme.colors.successHover,
        },
      },
      {
        appearance: 'outline',
        variant: 'warning',
        styles: {
          backgroundColor: theme.colors.warningHover,
        },
      },
    ],
  },
  button: {
    alignItems: 'center',
    borderRadius: theme.borderRadius.full,
    borderCurve: 'continuous',
    flexDirection: 'row',
    justifyContent: 'center',
    variants: {
      appearance: {
        ghost: {
          backgroundColor: 'transparent',
          borderWidth: 0,
          _web: {
            _hover: {
              backgroundColor: theme.colors.backgroundSecondary,
            },
          },
        },
        outline: {
          backgroundColor: 'transparent',
          borderWidth: 2,
        },
        solid: {},
      },
      disabled: {
        true: {
          opacity: 0.5,
        },
      },
      iconOnly: {
        true: {
          paddingHorizontal: 0,
          paddingVertical: 0,
        },
      },
      shape: {
        circle: {
          borderRadius: theme.borderRadius.full,
        },
        pill: {
          borderRadius: theme.borderRadius.full,
        },
      },
      size: {
        lg: {
          gap: theme.space('sm'),
          minHeight: theme.size(12),
          paddingHorizontal: theme.space('xl'),
          paddingVertical: theme.space('sm'),
        },
        md: {
          gap: theme.space('sm'),
          minHeight: theme.size(10),
          paddingHorizontal: theme.space('lg'),
          paddingVertical: theme.space('xs'),
        },
        sm: {
          gap: theme.space('xs'),
          minHeight: theme.size(8),
          paddingHorizontal: theme.space('md'),
          paddingVertical: theme.space('xs'),
        },
        xl: {
          gap: theme.space('sm'),
          minHeight: theme.size(14),
          paddingHorizontal: theme.space('2xl'),
          paddingVertical: theme.space('md'),
        },
        xs: {
          gap: theme.space('xs'),
          minHeight: theme.size(7),
          paddingHorizontal: theme.space('sm'),
          paddingVertical: theme.space('xs'),
        },
      },
      variant: {
        error: {
          backgroundColor: theme.colors.error,
          borderColor: theme.colors.error,
          _web: {
            _hover: {
              backgroundColor: theme.colors.errorHover,
            },
          },
        },
        info: {
          backgroundColor: theme.colors.info,
          borderColor: theme.colors.info,
          _web: {
            _hover: {
              backgroundColor: theme.colors.infoHover,
            },
          },
        },
        negative: {
          backgroundColor: theme.colors.error,
          borderColor: theme.colors.error,
          _web: {
            _hover: {
              backgroundColor: theme.colors.errorHover,
            },
          },
        },
        positive: {
          backgroundColor: theme.colors.success,
          borderColor: theme.colors.success,
          _web: {
            _hover: {
              backgroundColor: theme.colors.successHover,
            },
          },
        },
        primary: {
          backgroundColor: theme.colors.primary,
          borderColor: theme.colors.primary,
          _web: {
            _hover: {
              backgroundColor: theme.colors.primaryHover,
            },
          },
        },
        secondary: {
          backgroundColor: theme.colors.secondary,
          borderColor: theme.colors.secondary,
          _web: {
            _hover: {
              backgroundColor: theme.colors.secondaryHover,
            },
          },
        },
        success: {
          backgroundColor: theme.colors.success,
          borderColor: theme.colors.success,
          _web: {
            _hover: {
              backgroundColor: theme.colors.successHover,
            },
          },
        },
        warning: {
          backgroundColor: theme.colors.warning,
          borderColor: theme.colors.warning,
          _web: {
            _hover: {
              backgroundColor: theme.colors.warningHover,
            },
          },
        },
      },
    },
    compoundVariants: [
      {
        appearance: 'ghost',
        styles: {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          borderWidth: 0,
          _web: {
            _hover: {
              backgroundColor: theme.colors.backgroundSecondary,
            },
          },
        },
      },
      {
        appearance: 'outline',
        variant: 'error',
        styles: {
          backgroundColor: 'transparent',
          borderColor: theme.colors.error,
          _web: {
            _hover: {
              backgroundColor: theme.colors.errorMuted,
            },
          },
        },
      },
      {
        appearance: 'outline',
        variant: 'info',
        styles: {
          backgroundColor: 'transparent',
          borderColor: theme.colors.info,
          _web: {
            _hover: {
              backgroundColor: theme.colors.infoMuted,
            },
          },
        },
      },
      {
        appearance: 'outline',
        variant: 'negative',
        styles: {
          backgroundColor: 'transparent',
          borderColor: theme.colors.error,
          _web: {
            _hover: {
              backgroundColor: theme.colors.errorMuted,
            },
          },
        },
      },
      {
        appearance: 'outline',
        variant: 'positive',
        styles: {
          backgroundColor: 'transparent',
          borderColor: theme.colors.success,
          _web: {
            _hover: {
              backgroundColor: theme.colors.successMuted,
            },
          },
        },
      },
      {
        appearance: 'outline',
        variant: 'primary',
        styles: {
          backgroundColor: 'transparent',
          borderColor: theme.colors.primary,
          _web: {
            _hover: {
              backgroundColor: theme.colors.primaryMuted,
            },
          },
        },
      },
      {
        appearance: 'outline',
        variant: 'secondary',
        styles: {
          backgroundColor: 'transparent',
          borderColor: theme.colors.secondary,
          _web: {
            _hover: {
              backgroundColor: theme.colors.secondaryMuted,
            },
          },
        },
      },
      {
        appearance: 'outline',
        variant: 'success',
        styles: {
          backgroundColor: 'transparent',
          borderColor: theme.colors.success,
          _web: {
            _hover: {
              backgroundColor: theme.colors.successMuted,
            },
          },
        },
      },
      {
        appearance: 'outline',
        variant: 'warning',
        styles: {
          backgroundColor: 'transparent',
          borderColor: theme.colors.warning,
          _web: {
            _hover: {
              backgroundColor: theme.colors.warningMuted,
            },
          },
        },
      },
      {
        iconOnly: true,
        size: 'xs',
        styles: {
          minWidth: theme.size(7),
          paddingHorizontal: 0,
          paddingVertical: 0,
        },
      },
      {
        iconOnly: true,
        size: 'sm',
        styles: {
          minWidth: theme.size(8),
          paddingHorizontal: 0,
          paddingVertical: 0,
        },
      },
      {
        iconOnly: true,
        size: 'md',
        styles: {
          minWidth: theme.size(10),
          paddingHorizontal: 0,
          paddingVertical: 0,
        },
      },
      {
        iconOnly: true,
        size: 'lg',
        styles: {
          minWidth: theme.size(12),
          paddingHorizontal: 0,
          paddingVertical: 0,
        },
      },
      {
        iconOnly: true,
        size: 'xl',
        styles: {
          minWidth: theme.size(14),
          paddingHorizontal: 0,
          paddingVertical: 0,
        },
      },
    ],
  },
  overrides: ({
    alignSelf,
    backgroundColor,
    borderBottomWidth,
    borderColor,
    borderRadius,
    borderWidth,
    flex,
    gap,
    height,
    maxWidth,
    minHeight,
    minWidth,
    opacity,
    padding,
    paddingBottom,
    paddingHorizontal,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingVertical,
    width,
  }: ButtonStyleProps) => ({
    ...(alignSelf !== undefined ? { alignSelf } : {}),
    ...(backgroundColor !== undefined ? { backgroundColor } : {}),
    ...(borderBottomWidth !== undefined ? { borderBottomWidth } : {}),
    ...(borderColor !== undefined ? { borderColor } : {}),
    ...(borderRadius !== undefined ? { borderRadius } : {}),
    ...(borderWidth !== undefined ? { borderWidth } : {}),
    ...(flex !== undefined
      ? { flex: typeof flex === 'number' ? flex : flex ? 1 : undefined }
      : {}),
    ...(gap !== undefined ? { gap } : {}),
    ...(height !== undefined ? { height } : {}),
    ...(maxWidth !== undefined ? { maxWidth } : {}),
    ...(minHeight !== undefined ? { minHeight } : {}),
    ...(minWidth !== undefined ? { minWidth } : {}),
    ...(opacity !== undefined ? { opacity } : {}),
    ...(padding !== undefined ? { padding } : {}),
    ...(paddingBottom !== undefined ? { paddingBottom } : {}),
    ...(paddingHorizontal !== undefined ? { paddingHorizontal } : {}),
    ...(paddingLeft !== undefined ? { paddingLeft } : {}),
    ...(paddingRight !== undefined ? { paddingRight } : {}),
    ...(paddingTop !== undefined ? { paddingTop } : {}),
    ...(paddingVertical !== undefined ? { paddingVertical } : {}),
    ...(width !== undefined ? { width } : {}),
  }),
}));
